import React, { useEffect, useState } from 'react'
import styles from './Mock.module.scss'

const isPdf = contentType => contentType?.toLowerCase().includes('pdf')
const isText = contentType =>
  contentType?.toLowerCase().includes('text') ||
  contentType?.toLowerCase().includes('plain')

const PdfPage = ({ pdf, pageNumber }) => {
  const [failed, setFailed] = useState(false)
  const canvasRef = React.useRef(null)

  useEffect(() => {
    let cancelled = false
    let renderTask

    pdf
      .getPage(pageNumber)
      .then(page => {
        if (cancelled) return undefined

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const baseViewport = page.getViewport({ scale: 1 })
        const availableWidth = Math.max(1, canvas.parentElement.clientWidth)
        const scale = Math.min(1, availableWidth / baseViewport.width)
        const outputScale = window.devicePixelRatio || 1
        const viewport = page.getViewport({ scale: scale * outputScale })

        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.width = `${viewport.width / outputScale}px`
        canvas.style.height = `${viewport.height / outputScale}px`

        renderTask = page.render({ canvasContext: context, viewport })
        return renderTask.promise
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      renderTask?.cancel()
    }
  }, [pdf, pageNumber])

  if (failed) return <p role="alert">Failed to render PDF page.</p>
  return (
    <div role="img" aria-label={`PDF page ${pageNumber}`}>
      <canvas ref={canvasRef} className={styles.pdfPage} aria-hidden="true" />
    </div>
  )
}

export const useDocViewer = () => {
  const [previewDoc, setPreviewDoc] = useState(null)
  return {
    previewDoc,
    openDoc: setPreviewDoc,
    closeDoc: () => setPreviewDoc(null),
  }
}

export const DocViewerModal = ({ doc, url, pdfData, onClose }) => {
  const [textContent, setTextContent] = useState(null)
  const [pdf, setPdf] = useState(null)
  const [pdfError, setPdfError] = useState(false)

  useEffect(() => {
    if (isText(doc.contentType)) {
      fetch(url)
        .then(r => r.text())
        .then(setTextContent)
        .catch(() => setTextContent('Failed to load document.'))
    }
  }, [url, doc.contentType])

  useEffect(() => {
    if (!isPdf(doc.contentType)) return undefined

    let cancelled = false
    let loadingTask

    import('pdfjs-dist/webpack')
      .then(({ getDocument }) => {
        if (cancelled) return undefined
        const source = pdfData ? { data: pdfData } : { url }
        loadingTask = getDocument(source)
        return loadingTask.promise
      })
      .then(document => {
        if (!document) return
        if (cancelled) {
          document.destroy()
          return
        }
        setPdf(document)
      })
      .catch(error => {
        console.error('Failed to load PDF document.', error)
        if (!cancelled) setPdfError(true)
      })

    return () => {
      cancelled = true
      loadingTask?.destroy()
    }
  }, [url, pdfData, doc.contentType])

  return (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={e => e.target === e.currentTarget && onClose()}
      onKeyDown={e => e.key === 'Escape' && onClose()}
    >
      <div className={styles.modalBox} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <span>{doc.source || 'Document'}</span>
          <button type="button" className={styles.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          {isPdf(doc.contentType) && (
            <div className={styles.pdfDocument}>
              {pdfError && <p role="alert">Failed to load document.</p>}
              {!pdf && !pdfError && <p>Loading document…</p>}
              {pdf &&
                Array.from({ length: pdf.numPages }, (_, index) => (
                  <PdfPage key={index + 1} pdf={pdf} pageNumber={index + 1} />
                ))}
            </div>
          )}
          {isText(doc.contentType) && (
            <pre className={styles.textContent}>
              {textContent ?? 'Loading…'}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

export const docOpensInViewer = contentType =>
  isPdf(contentType) || isText(contentType)
